import { msg } from "@lingui/core/macro";
import { createMemo, Show } from "solid-js";
import { UserCheck } from "@fleet/icons";
import { getListAssigneesQuery, type ListAssignee, queryKeys } from "@listto/shared";
import { createPowerSyncQuery } from "@listto/shared/solid";
import { GlassSurface } from "@fleet/ui";
import { Select } from "@/components/ui/select";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";

interface AssigneeSectionProps {
  /** Todo's list (assignment only applies to shared lists). */
  listId: string | null;
  /** Current assignee's user_id (null = unassigned). */
  assignedTo: string | null;
  /** Persists assignment change (write-path validated server-side). */
  onAssign: (userId: string | null) => void;
}

/** Sentinel Select value for "unassigned" (Select does not accept null). */
const UNASSIGNED = "__unassigned__";

interface AssigneeOption {
  value: string;
  label: string;
}

function sameOptions(prev: AssigneeOption[], next: AssigneeOption[]): boolean {
  return (
    prev.length === next.length &&
    prev.every((o, i) => o.value === next[i]?.value && o.label === next[i]?.label)
  );
}

/**
 * Task assignment to a member: visible ONLY when the list is
 * shared via organization (there are locally synced members). In
 * personal lists it is not shown — there is nobody to assign to.
 */
export function AssigneeSection(props: AssigneeSectionProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();

  const assignees = createPowerSyncQuery({
    queryKey: () => queryKeys.listAssignees.byList(props.listId ?? undefined),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance && props.listId ? getListAssigneesQuery(dbInstance, props.listId) : null;
    },
    enabled: () => Boolean(drizzle() && props.listId),
  });

  const members = () => (assignees.data ?? []) as ListAssignee[];

  // The query re-emits on every synced change. A new array of new objects
  // makes the Select re-create its listbox items, so an option that is being
  // clicked is detached from the DOM mid-click. Keep the previous array while
  // the content is unchanged.
  const options = createMemo(
    (): AssigneeOption[] => [
      { value: UNASSIGNED, label: t(msg`Unassigned`) },
      ...members().map((m) => ({
        value: m.id,
        label: m.name || m.email || m.id,
      })),
    ],
    [],
    { equals: sameOptions },
  );

  // The assignee may have left the org: still displayed to avoid
  // visually "losing" existing assignment.
  const selectValue = createMemo(() => {
    const current = props.assignedTo;
    if (!current) {
      return UNASSIGNED;
    }
    return options().some((o) => o.value === current) ? current : UNASSIGNED;
  });

  return (
    <Show when={props.listId && members().length > 0}>
      <GlassSurface class="flex flex-col p-1.5 mt-2 gap-1">
        <div class="flex items-center gap-2 px-3 pt-3 pb-1 text-sm font-semibold text-muted-foreground">
          <div class="flex justify-center w-5 shrink-0">
            <UserCheck class="h-4.5 w-4.5" />
          </div>
          {t(msg`Assigned to`)}
        </div>
        <div class="px-3 pb-2" data-testid="todo-assignee-select">
          <Select
            options={options()}
            value={selectValue()}
            onChange={(value) => {
              props.onAssign(value === UNASSIGNED ? null : value);
            }}
            aria-label={t(msg`Assigned to`)}
          />
        </div>
      </GlassSurface>
    </Show>
  );
}
